import {
  IconBuildingWarehouse,
  IconChartBar,
  IconFileInvoice,
  IconHome,
  IconPackage,
  IconPlus,
  IconReceipt,
  IconSettings,
  IconShieldCheck,
  IconShip,
  IconSpeakerphone,
  IconTarget,
  IconTruck,
  IconUsers,
  IconUsersGroup,
  IconBook,
} from '@tabler/icons-react';
import { AppShell, PageHeader, type NavGroup, type Zone } from '@hana/shell';
import { Button, Chip, StateView, Table, type ScreenState } from '@hana/ui';
import s from './Kit.module.css';

// /ui-kit/shell/* — khung app mẫu với menu đầy đủ của board Mint-Home (NAVK), để chụp so board.
// Không phải vùng thật; ?state=loading|empty|error|noperm đổi trạng thái màn.

const B = '/ui-kit/shell';
const g = (key: string, label: string, icon: NavGroup['icon'], items: [string, string, number?][], section?: string): NavGroup => ({
  key,
  label,
  icon,
  section,
  items: items.map(([k, l, n]) => ({ key: k, label: l, to: `${B}/${k}`, n })),
});

const DEMO: Zone = {
  key: 'ezy',
  name: 'HANA Ezy',
  top: [{ key: 'home', label: 'Trang chủ', to: B, icon: IconHome, n: 8 }],
  nav: [
    g('kh', 'Khách hàng', IconUsers, [['customers', 'Khách hàng'], ['partner', 'Đối tác'], ['contract', 'Hợp đồng dịch vụ']], 'CRM'),
    g('kd', 'Bán hàng', IconTarget, [['sales', 'Hôm nay của Sale'], ['pipeline', 'Cơ hội'], ['quote', 'Báo giá', 17]]),
    g('mk', 'Marketing', IconSpeakerphone, [['mkt', 'Marketing & quà'], ['share', 'Market Share']]),
    g('gia', 'Giá & lịch tàu', IconShip, [['p2p', 'Giá và Lịch'], ['rates', 'Freight Cost'], ['map', 'Services Map'], ['inq', 'Hỏi giá đối tác', 5], ['setup', 'Thiết lập giá'], ['news', 'Tin thị trường']], 'Nghiệp vụ'),
    g('job', 'Job', IconPackage, [['jobs', 'Danh sách Job', 186], ['booking', 'Booking', 42], ['bl', 'Chứng từ & B/L'], ['import', 'Hàng nhập & cont']]),
    g('dv', 'Dịch vụ', IconShieldCheck, [['service', 'Service Hub'], ['hs', 'Tra HS'], ['customs', 'Customs Data']]),
    g('vt', 'Vận tải', IconTruck, [['tms', 'Điều độ'], ['dispatch', 'Lưới điều xe'], ['routes', 'Tuyến']]),
    g('ban', 'Bán ra', IconFileInvoice, [['invoice', 'Hoá đơn', 23], ['ar', 'Công nợ', 41], ['pay', 'Thanh toán']], 'Tài chính'),
    g('mua', 'Mua vào', IconReceipt, [['cost', 'Chi phí & Costing'], ['ap', 'Phải trả'], ['settle', 'Quyết toán']]),
    g('bod', 'BOD', IconChartBar, [['bod', 'Báo cáo BOD'], ['kpi', 'KPI']]),
    g('ns', 'Nhân sự', IconUsersGroup, [['hr', 'Nhân sự'], ['profile', 'Hồ sơ']], 'Nội bộ'),
    g('tv', 'Thư viện', IconBook, [['files', 'Tài liệu & media'], ['sop', 'SOP & hướng dẫn']]),
    g('qt', 'Quản trị', IconSettings, [['admin', 'Quản trị'], ['log', 'Action Log']]),
  ],
  routes: () => [],
};

interface Row {
  id: string;
  cus: string;
  route: string;
  st: string;
  dot: string;
}
const ROWS: Row[] = [
  { id: 'SE260622020', cus: 'GREENBUD', route: 'VNSGN → KRPUS', st: 'Active', dot: 'var(--d-blue)' },
  { id: 'SE260631180', cus: 'Dệt may An Phú', route: 'VNCLI → USLAX', st: 'Standby', dot: 'var(--d-amber)' },
  { id: 'SE260629040', cus: 'THANHHAO', route: 'VNSGN → JPTYO', st: 'Released', dot: 'var(--d-green)' },
];

export function ShellDemo() {
  const state = (new URLSearchParams(location.search).get('state') ?? 'ready') as ScreenState;
  return (
    <AppShell zone={DEMO} user={{ name: 'David Nguyen', email: 'david@hanalogisticsvn.com', title: '[SALE-MGR] DAVID' }} onSignOut={() => {}}>
      <PageHeader
        title="Trang chủ"
        sub="Thứ Năm, 29/Oct/2026"
        actions={
          <Button variant="primary" icon={<IconPlus size={16} />}>
            Tạo job
          </Button>
        }
      />
      <section className={s.card}>
        <StateView
          state={state}
          title={state === 'empty' ? 'Chưa có job hôm nay' : undefined}
          action={state === 'error' ? <Button variant="secondary">Thử lại</Button> : undefined}
        >
          <h2>Việc của tôi</h2>
          <Table
            label="Job"
            rows={ROWS}
            rowKey={(r) => r.id}
            columns={[
              { key: 'id', header: 'Số job', width: 130, cell: (r) => <b className={s.id}>{r.id}</b> },
              { key: 'cus', header: 'Khách hàng', minor: true, cell: (r) => r.cus },
              { key: 'route', header: 'Tuyến', width: 170, minor: true, cell: (r) => r.route },
              { key: 'st', header: 'Trạng thái', width: 130, cell: (r) => <Chip label="Trạng thái" dot={r.dot}>{r.st}</Chip> },
            ]}
          />
        </StateView>
      </section>
      {state === 'ready' &&
        [1, 2, 3].map((i) => (
          <section key={i} className={s.card} style={{ minHeight: 220 }}>
            <h2>
              <IconBuildingWarehouse size={18} /> Khối {i}
            </h2>
          </section>
        ))}
    </AppShell>
  );
}
